import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AlertCircle, Save } from 'react-feather';
import config from '../../../../config';
import ModalShell from '../shared/ModalShell';

const empty = {
    property_name: '',
    property_country_id: '',
    property_area: '',
    property_mall_code: '',
    property_address: '',
    property_mall_currency: '',
    property_stores: '',
    property_disable: false,
};

/**
 * Add / Edit modal for the Property entity.
 *
 * Floor assignment lives on a richer dedicated page; this modal handles the
 * core property metadata only (name, country, area, mall code, address,
 * currency, stores, disable flag) — the same fields surfaced in the legacy
 * NewProperty / PropertyEdit screens minus the Typeahead floors picker,
 * which we'll wire back in once Property gets its own detail drawer.
 */
const PropertyFormModal = ({ mode = 'add', entityId, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState(empty);
    const [countries, setCountries] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const concessionaireId = localStorage.getItem('concessionaireId');
    const loginId = localStorage.getItem('loginId');
    const loginName = localStorage.getItem('loginName');
    const token = localStorage.getItem('sessiontoken');
    const headers = { Authorization: `${token}` };

    useEffect(() => {
        let cancelled = false;
        const fetches = [
            axios.post(
                `${config.hostname}/country/getcountrybyconcessionaire`,
                { country_concessionaire_id: concessionaireId },
                { headers },
            ),
        ];
        if (mode === 'edit' && entityId) {
            fetches.push(axios.get(`${config.hostname}/property/${entityId}`, { headers }));
        }
        Promise.all(fetches)
            .then(([countryRes, propRes]) => {
                if (cancelled) return;
                setCountries(Array.isArray(countryRes.data) ? countryRes.data : []);
                if (propRes) {
                    const d = propRes.data;
                    setForm({
                        property_name: d.property_name || '',
                        property_country_id: d.property_country_id?._id || d.property_country_id || '',
                        property_area: d.property_area ?? '',
                        property_mall_code: d.property_mall_code || '',
                        property_address: d.property_address || '',
                        property_mall_currency: d.property_mall_currency || '',
                        property_stores: d.property_stores ?? '',
                        property_disable: !!d.property_disable,
                    });
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error(t('Failed to load form'));
                onClose?.();
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [mode, entityId]);

    const set = (k) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: value }));
    };

    const validate = () => {
        const next = {};
        if (!form.property_name.trim()) next.property_name = t('Property name is required');
        if (!form.property_country_id) next.property_country_id = t('Country is required');
        if (form.property_area === '' || Number(form.property_area) <= 0) next.property_area = t('Area must be a positive number');
        if (!form.property_mall_code.trim()) next.property_mall_code = t('Mall code is required');
        if (!form.property_address.trim()) next.property_address = t('Address is required');
        if (!form.property_mall_currency.trim()) next.property_mall_currency = t('Currency is required');
        if (form.property_stores === '' || Number(form.property_stores) < 0) next.property_stores = t('Stores must be a number');
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);

        const payload = {
            ...form,
            property_concessionaire_id: concessionaireId,
            login_id: loginId,
        };
        if (mode === 'add') {
            payload.property_created_date = new Date().toISOString().substr(0, 10);
            payload.property_created_by = loginName;
        }

        const req = mode === 'add'
            ? axios.post(`${config.hostname}/property`, payload, { headers })
            : axios.post(`${config.hostname}/property/updateproperty/${entityId}`, payload, { headers });

        req
            .then(() => {
                toast.success(mode === 'add' ? t('Property added') : t('Property updated'));
                onSaved?.();
                onClose?.();
            })
            .catch((err) => {
                console.error(err);
                toast.error(t('Save failed'));
            })
            .finally(() => setSaving(false));
    };

    return (
        <ModalShell title={mode === 'add' ? t('Add property') : t('Edit property')} onClose={onClose} maxWidth={760}>
            {loading ? (
                <div className="v3-sadmin-empty"><p>{t('Loading…')}</p></div>
            ) : (
                <form className="v3-sadmin-form" onSubmit={handleSubmit}>
                    <div className="v3-sadmin-form-grid">
                        <label className="v3-field">
                            <span className="v3-label">{t('Property name')}</span>
                            <input
                                className="v3-input"
                                value={form.property_name}
                                onChange={set('property_name')}
                                placeholder={t('Marina Mall')}
                                autoFocus
                            />
                            {errors.property_name && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.property_name}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Country')}</span>
                            <div className="v3-select-wrap">
                                <select
                                    className="v3-input v3-select"
                                    value={form.property_country_id}
                                    onChange={set('property_country_id')}
                                >
                                    <option value="">{t('Select country…')}</option>
                                    {countries.map((c) => (
                                        <option key={c._id} value={c._id}>{c.country_name}</option>
                                    ))}
                                </select>
                            </div>
                            {errors.property_country_id && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.property_country_id}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Mall code')}</span>
                            <input
                                className="v3-input"
                                value={form.property_mall_code}
                                onChange={set('property_mall_code')}
                                placeholder="MM-001"
                            />
                            {errors.property_mall_code && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.property_mall_code}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Currency')}</span>
                            <input
                                className="v3-input"
                                value={form.property_mall_currency}
                                onChange={set('property_mall_currency')}
                                placeholder="AED"
                                maxLength={8}
                            />
                            {errors.property_mall_currency && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.property_mall_currency}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Area (sq ft)')}</span>
                            <input
                                className="v3-input"
                                type="number"
                                value={form.property_area}
                                onChange={set('property_area')}
                                placeholder="120000"
                            />
                            {errors.property_area && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.property_area}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Stores')}</span>
                            <input
                                className="v3-input"
                                type="number"
                                value={form.property_stores}
                                onChange={set('property_stores')}
                                placeholder="250"
                            />
                            {errors.property_stores && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.property_stores}</span>
                            )}
                        </label>
                    </div>
                    <label className="v3-field">
                        <span className="v3-label">{t('Address')}</span>
                        <input
                            className="v3-input"
                            value={form.property_address}
                            onChange={set('property_address')}
                            placeholder={t('Sheikh Zayed Road, Dubai')}
                        />
                        {errors.property_address && (
                            <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.property_address}</span>
                        )}
                    </label>
                    <label className="v3-sadmin-checkbox-row">
                        <input
                            type="checkbox"
                            checked={form.property_disable}
                            onChange={set('property_disable')}
                        />
                        {t('Disable this property (hidden from analytics)')}
                    </label>
                    <div className="v3-sadmin-form-actions">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={onClose} disabled={saving}>
                            {t('Cancel')}
                        </button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                            <Save size={14} /> {saving ? t('Saving…') : mode === 'add' ? t('Add property') : t('Save changes')}
                        </button>
                    </div>
                </form>
            )}
        </ModalShell>
    );
};

export default PropertyFormModal;
